import '@formatjs/intl-getcanonicallocales/polyfill';
import '@formatjs/intl-locale/polyfill';
import 'array.prototype.at/auto';
import 'array.prototype.findlastindex/auto';
import 'array.prototype.flat/auto';
import 'array.prototype.flatmap/auto';
import 'object.fromentries/auto';
import 'promise.allsettled/auto';
import 'promise.withresolvers/auto';
// api
import '@formatjs/intl-numberformat/polyfill';
// langs
import '@formatjs/intl-numberformat/locale-data/de';
import '@formatjs/intl-numberformat/locale-data/en';
import '@formatjs/intl-numberformat/locale-data/fr';
import '@formatjs/intl-numberformat/locale-data/nl';

// api
import '@formatjs/intl-pluralrules/polyfill';
// langs
import '@formatjs/intl-pluralrules/locale-data/de';
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/fr';
import '@formatjs/intl-pluralrules/locale-data/nl';

import * as Sentry from '@sentry/react';
import '@stripe/stripe-js';
import '@ungap/global-this';
import * as config from 'config';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';
import isBetween from 'dayjs/plugin/isBetween';
import minMax from 'dayjs/plugin/minMax';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import i18n from 'i18n';
import { get } from 'lodash';
import { parse } from 'query-string';
import RelativeTimeFormat from 'relative-time-format';
import de from 'relative-time-format/locale/de';
import en from 'relative-time-format/locale/en';
import fr from 'relative-time-format/locale/fr';
import nl from 'relative-time-format/locale/nl';
import { initAmplitude, logAmplitude } from 'utils/amplitude';

RelativeTimeFormat.addLocale(en);
RelativeTimeFormat.addLocale(fr);
RelativeTimeFormat.addLocale(de);
RelativeTimeFormat.addLocale(nl);

i18n.on('languageChanged', (lng) => {
  dayjs.locale(lng);
  try {
    document.getElementsByTagName('html')[0].setAttribute('lang', lng);
  } catch (e) {
    console.error(e);
    Sentry.captureException(e);
  }
});

/** TODO: Setup dayjs with centralized configuration. Just uncomment the line
 * below and remove dayjs.extend line in src/i18n/config.ts
 */
// import 'config/dayjs';
dayjs.extend(quarterOfYear);
dayjs.extend(relativeTime);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(minMax);

initAmplitude();

const THIRD_PARTY_PATTERNS = [
  'pixel.js',
  'taboola.com',
  'connect.facebook.net',
  'googletagmanager.com',
  'google-analytics.com',
  'doubleclick.net',
];

const TIMEOUT_PATTERNS = [
  'timeout',
  'ETIMEDOUT',
  'Request timed out',
  'The operation timed out',
  'AbortError: timeout',
];

if (config.environment !== 'development') {
  Sentry.init({
    dsn: 'https://4b5091c3d6434100a96cf19d0d9a51bd@o1135119.ingest.sentry.io/6465173',
    environment: config.environment,
    release: config.sentryRelease,
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,

      /connect\.facebook\.net/i,
      /taboola\.com/i,
      /googletagmanager\.com/i,
      /doubleclick\.net/i,
    ],

    // 2️⃣ Known safe noise
    ignoreErrors: [
      // Vite / dynamic import noise
      /Failed to fetch dynamically imported module/i,
      /Error importing a module script failed/i,
      /Loading chunk \d+ failed/i,
      'ChunkLoadError',

      // React Query / fetch noise
      'AbortError',
      'The user aborted a request',
      'NetworkError when attempting to fetch resource',

      // Browser quirks
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',

      // Cross-origin iframe noise
      'Blocked a frame with origin',
      'Permission denied to access property',
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;

      const exception = event.exception?.values?.[0];
      const message = exception?.value ?? '';
      const ua = navigator.userAgent.toLowerCase();

      // filter out mobile browser noise

      const isMobile =
        ua.includes('android') ||
        ua.includes('iphone') ||
        ua.includes('ipad') ||
        ua.includes('mobile');

      if (isMobile) {
        return null; // ❌ Drop event (won’t be sent to Sentry)
      }

      if (
        TIMEOUT_PATTERNS.some((pattern) =>
          message.toLowerCase().includes(pattern.toLowerCase()),
        )
      ) {
        return null; // ❌ Drop timeout error
      }

      // 1️⃣ Check exception message
      if (THIRD_PARTY_PATTERNS.some((pattern) => message.includes(pattern))) {
        return null;
      }

      // 2️⃣ Check stacktrace frames
      const frames = exception?.stacktrace?.frames || [];
      for (const frame of frames) {
        const filename = frame.filename || '';
        if (
          THIRD_PARTY_PATTERNS.some((pattern) => filename.includes(pattern))
        ) {
          return null;
        }
      }
      // filter unhandled promise rejections
      if (
        exception?.type &&
        ['UnhandledPromiseRejection', 'UnhandledRejection'].includes(
          exception?.type,
        ) &&
        !exception?.value
      ) {
        return null;
      }

      if (error instanceof Error) {
        if (
          error.message
            ?.toLowerCase()
            .includes('importing a module script failed')
        ) {
          return null;
        }
      }

      // Handle non-Error exceptions (strings, etc.)
      if (typeof error === 'string') {
        if (error.toLowerCase().includes('importing a module script failed')) {
          return null;
        }
      }

      if (error && get(error, 'response')) {
        if (
          // wrong email or password
          (get(error, 'response.status') === 401 &&
            get(error, 'response.request.responseURL').includes(
              '/v2/users/login',
            )) ||
          // filter  AI stream errors
          (get(error, 'response.status') === 400 &&
            get(error, 'response.request.responseURL').includes('/stream')) ||
          // filter client backend existence check errors
          (get(error, 'response.status') === 400 &&
            get(error, 'response.request.responseURL').includes(
              '/v2/clients',
            )) ||
          get(error, 'response.data.errors.[0].code') === 'rate-limit-exceeded'
        ) {
          // Skip reporting this event
          return null;
        }
      }

      return event;
    },
    beforeBreadcrumb: (breadcrum, hint) => {
      if (breadcrum.category === 'xhr') {
        breadcrum.data = {
          ...breadcrum.data,
          ...(hint?.xhr?.status >= 400
            ? {
                response: hint?.xhr?.responseText,
                payload: hint?.input,
              }
            : {}),
        };
      }
      return breadcrum;
    },
    // integrations: [
    //   new BrowserTracing({
    //     tracingOrigins: [/.*\.accountable.eu/, /^\//],
    //     routingInstrumentation: Sentry.reactRouterV6Instrumentation(
    //       useEffect,
    //       useLocation,
    //       useNavigationType,
    //       createRoutesFromChildren,
    //       matchRoutes,
    //     ),
    //   }),
    // ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: config.environment == 'production' ? 0.01 : 1,
    maxValueLength: 8192,
    normalizeDepth: 10,
  });
}

logAmplitude('WEBAPP_SCREEN_SPECS', {
  screenResolution: `${window.screen.width}x${window.screen.height}`,
  deviceResolution: `${window.screen.width * window.devicePixelRatio}x${
    window.screen.height * window.devicePixelRatio
  }`,
  viewportWidth: `App width:${window.innerWidth.toString()}px`,
  viewportHeight: `App height:${window.innerHeight.toString()}px`,
});

(window as any).shouldOpenIntercomInitially =
  parse(window.location.search).utm_medium === 'chat_opener';
